import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import DnerhsApi from "api/DnerhsApi";
import Alert from "components/Alert";
import history from "utils/History";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DescriptionIcon from "@material-ui/icons/Description";
import SchoolIcon from "@material-ui/icons/School";
import BusinessIcon from "@material-ui/icons/Business";
import PersonIcon from "@material-ui/icons/Person";
import DateRangeIcon from "@material-ui/icons/DateRange";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import HomeIcon from "@material-ui/icons/Home";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import { trackPromise } from "react-promise-tracker";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  card: {
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  infoRow: {
    display: "flex",
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    minWidth: "220px",
    color: "#555",
  },
  value: {
    color: "#333",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  estadoChip: {
    fontWeight: 600,
  },
}));

const DetalleConvenio = () => {
  const classes = useStyles();
  const { convenioId } = useParams();
  const [convenio, setConvenio] = useState(null);
  const [carreras, setCarreras] = useState([]);
  const [practicas, setPracticas] = useState([]);
  const [rector, setRector] = useState(null);
  const [director, setDirector] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [convenioId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Obtener datos del convenio
      const convenioResponse = await trackPromise(
        DnerhsApi.get(`/convenios/${convenioId}`)
      );
      setConvenio(convenioResponse.data);

      // Obtener carreras/programas del convenio
      try {
        const carrerasResponse = await DnerhsApi.get(
          `/convenios/${convenioId}/carreras-programas`
        );
        setCarreras(carrerasResponse.data);
      } catch (error) {
        console.log("No se pudieron cargar las carreras");
      }

      // Obtener prácticas del convenio
      try {
        const practicasResponse = await DnerhsApi.get(
          `/convenios/${convenioId}/practicas`
        );
        setPracticas(practicasResponse.data);
      } catch (error) {
        console.log("No se pudieron cargar las prácticas");
      }

      // Obtener datos del rector
      try {
        const rectorResponse = await DnerhsApi.get(
          `/convenios/${convenioId}/rector`
        );
        setRector(rectorResponse.data);
      } catch (error) {
        console.log("No se pudo cargar el rector");
      }

      // Obtener datos del director
      try {
        const directorResponse = await DnerhsApi.get(
          `/convenios/${convenioId}/director`
        );
        setDirector(directorResponse.data);
      } catch (error) {
        console.log("No se pudo cargar el director");
      }

    } catch (error) {
      console.error("Error al cargar datos:", error);
      Alert.showServerError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    history.goBack();
  };

  const handleVerPractica = (practicaId) => {
    history.push(`/practicas-convenio/detalle/${practicaId}`);
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!convenio) {
    return (
      <Box className={classes.root}>
        <Typography variant="h6" color="error">
          No se encontró el convenio
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleVolver}
          style={{ marginTop: 16 }}
        >
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* HEADER */}
      <Box className={classes.header}>
        <Box className={classes.title}>
          <DescriptionIcon color="primary" fontSize="large" />
          <Typography variant="h4">
            Detalle de Convenio #{convenioId}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleVolver}
        >
          Volver
        </Button>
      </Box>

      {/* INFORMACIÓN GENERAL DEL CONVENIO */}
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            <DescriptionIcon />
            Información General del Convenio
          </Typography>
          <Divider style={{ marginBottom: 16 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>ID Convenio:</Typography>
                <Typography className={classes.value}>{convenio.id}</Typography>
              </Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Categoría:</Typography>
                <Chip
                  label={convenio.categoria || "N/A"}
                  color="primary"
                  size="small"
                  className={classes.estadoChip}
                />
              </Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Fecha Creación:</Typography>
                <Typography className={classes.value}>
                  {convenio.fechaCreacion ? new Date(convenio.fechaCreacion).toLocaleDateString() : "N/A"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Fecha Inicio Vigencia:</Typography>
                <Typography className={classes.value}>
                  {convenio.fechaInicioVigencia || "N/A"}
                </Typography>
              </Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Fecha Fin Vigencia:</Typography>
                <Typography className={classes.value}>
                  {convenio.fechaFinVigencia || "N/A"}
                </Typography>
              </Box>
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>Estado Vigencia:</Typography>
                <Chip
                  label={
                    convenio.fechaFinVigencia && new Date(convenio.fechaFinVigencia) < new Date()
                      ? "Vencido"
                      : "Vigente"
                  }
                  color={
                    convenio.fechaFinVigencia && new Date(convenio.fechaFinVigencia) < new Date()
                      ? "secondary"
                      : "primary"
                  }
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* INSTITUCIÓN FORMADORA */}
      {convenio.institucionFormadora && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              <SchoolIcon />
              Institución Formadora
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box className={classes.infoRow}>
                  <SchoolIcon style={{ fontSize: 18, marginRight: 8, color: "#1976d2" }} />
                  <Typography className={classes.label}>Nombre:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.institucion || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <BusinessIcon style={{ fontSize: 18, marginRight: 8, color: "#1976d2" }} />
                  <Typography className={classes.label}>Tipo:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.tipoInstitucionFormadora || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <EmailIcon style={{ fontSize: 18, marginRight: 8, color: "#1976d2" }} />
                  <Typography className={classes.label}>Email:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.email || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <HomeIcon style={{ fontSize: 18, marginRight: 8, color: "#1976d2" }} />
                  <Typography className={classes.label}>Sede:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.sede || "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box className={classes.infoRow}>
                  <LocationOnIcon style={{ fontSize: 18, marginRight: 8, color: "#1976d2" }} />
                  <Typography className={classes.label}>Dirección:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.direccion || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <PhoneIcon style={{ fontSize: 18, marginRight: 8, color: "#1976d2" }} />
                  <Typography className={classes.label}>Teléfono:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.telefono || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <LocationOnIcon style={{ fontSize: 18, marginRight: 8, color: "#2e7d32" }} />
                  <Typography className={classes.label}>Ciudad:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.ciudad?.nombre || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <LocationOnIcon style={{ fontSize: 18, marginRight: 8, color: "#2e7d32" }} />
                  <Typography className={classes.label}>Departamento:</Typography>
                  <Typography className={classes.value}>
                    {convenio.institucionFormadora.departamento?.nombre || "N/A"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* AUTORIDAD MÁXIMA (RECTOR) */}
      {rector && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              <AccountBalanceIcon />
              Autoridad Máxima (Rector)
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box className={classes.infoRow}>
                  <PersonIcon style={{ fontSize: 18, marginRight: 8, color: "#d32f2f" }} />
                  <Typography className={classes.label}>Nombre:</Typography>
                  <Typography className={classes.value}>
                    {rector.persona?.nombre && rector.persona?.apellido
                      ? `${rector.persona.nombre} ${rector.persona.apellido}`
                      : "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <BusinessIcon style={{ fontSize: 18, marginRight: 8, color: "#d32f2f" }} />
                  <Typography className={classes.label}>Cédula:</Typography>
                  <Typography className={classes.value}>
                    {rector.persona?.cedulaIdentidad
                      ? rector.persona.cedulaIdentidad.toLocaleString("es-PY")
                      : "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <SchoolIcon style={{ fontSize: 18, marginRight: 8, color: "#d32f2f" }} />
                  <Typography className={classes.label}>Profesión:</Typography>
                  <Typography className={classes.value}>
                    {rector.profesion || "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box className={classes.infoRow}>
                  <PhoneIcon style={{ fontSize: 18, marginRight: 8, color: "#d32f2f" }} />
                  <Typography className={classes.label}>Teléfono:</Typography>
                  <Typography className={classes.value}>
                    {rector.persona?.telefono || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <EmailIcon style={{ fontSize: 18, marginRight: 8, color: "#d32f2f" }} />
                  <Typography className={classes.label}>Email:</Typography>
                  <Typography className={classes.value}>
                    {rector.persona?.email || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <LocationOnIcon style={{ fontSize: 18, marginRight: 8, color: "#d32f2f" }} />
                  <Typography className={classes.label}>Dirección Rectoría:</Typography>
                  <Typography className={classes.value}>
                    {rector.direccionRectoria || "N/A"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* RESPONSABLE (DIRECTOR) */}
      {director && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              <PersonIcon />
              Responsable (Director)
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box className={classes.infoRow}>
                  <PersonIcon style={{ fontSize: 18, marginRight: 8, color: "#f57c00" }} />
                  <Typography className={classes.label}>Nombre:</Typography>
                  <Typography className={classes.value}>
                    {director.persona?.nombre && director.persona?.apellido
                      ? `${director.persona.nombre} ${director.persona.apellido}`
                      : "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <BusinessIcon style={{ fontSize: 18, marginRight: 8, color: "#f57c00" }} />
                  <Typography className={classes.label}>Cédula:</Typography>
                  <Typography className={classes.value}>
                    {director.persona?.cedulaIdentidad
                      ? director.persona.cedulaIdentidad.toLocaleString("es-PY")
                      : "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <SchoolIcon style={{ fontSize: 18, marginRight: 8, color: "#f57c00" }} />
                  <Typography className={classes.label}>Profesión:</Typography>
                  <Typography className={classes.value}>
                    {director.profesion || "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box className={classes.infoRow}>
                  <PhoneIcon style={{ fontSize: 18, marginRight: 8, color: "#f57c00" }} />
                  <Typography className={classes.label}>Teléfono:</Typography>
                  <Typography className={classes.value}>
                    {director.persona?.telefono || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <EmailIcon style={{ fontSize: 18, marginRight: 8, color: "#f57c00" }} />
                  <Typography className={classes.label}>Email:</Typography>
                  <Typography className={classes.value}>
                    {director.persona?.email || "N/A"}
                  </Typography>
                </Box>
                <Box className={classes.infoRow}>
                  <SchoolIcon style={{ fontSize: 18, marginRight: 8, color: "#f57c00" }} />
                  <Typography className={classes.label}>Carrera:</Typography>
                  <Typography className={classes.value}>
                    {director.carrera || "N/A"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* CARRERAS/PROGRAMAS */}
      {carreras && carreras.length > 0 && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              <SchoolIcon />
              Carreras/Programas ({carreras.length})
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell><strong>Carrera/Programa</strong></TableCell>
                    <TableCell><strong>Malla Curricular</strong></TableCell>
                    <TableCell><strong>Postgrado</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carreras.map((carrera, index) => (
                    <TableRow key={index}>
                      <TableCell>{carrera.carreraPrograma?.descripcion || "N/A"}</TableCell>
                      <TableCell>{carrera.mallaCurricular || "N/A"}</TableCell>
                      <TableCell>{carrera.nombrePostgrado?.descripcion || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* PRÁCTICAS ASOCIADAS */}
      {practicas && practicas.length > 0 && (
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" className={classes.sectionTitle}>
              <BusinessIcon />
              Prácticas Asociadas ({practicas.length})
            </Typography>
            <Divider style={{ marginBottom: 16 }} />

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell><strong>Materia</strong></TableCell>
                    <TableCell><strong>Estado</strong></TableCell>
                    <TableCell><strong>Fecha Desde</strong></TableCell>
                    <TableCell><strong>Fecha Hasta</strong></TableCell>
                    <TableCell><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {practicas.map((practica, index) => (
                    <TableRow key={index}>
                      <TableCell>{practica.id}</TableCell>
                      <TableCell>{practica.materia || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={practica.estado || "Sin estado"}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{practica.fechaDesde || "N/A"}</TableCell>
                      <TableCell>{practica.fechaHasta || "N/A"}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleVerPractica(practica.id)}
                        >
                          Ver Detalle
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DetalleConvenio;

